/* eslint-disable no-lonely-if */
import React, { useState, useContext, useEffect } from 'react';
import Checkbox from '@material-ui/core/Checkbox';
import TextField from '@material-ui/core/TextField';
import { find } from 'lodash-es';
import { SuggestBox } from '@mummssoftware/common-ui';
import { SuggestBoxWrapper } from '@mummssoftware/common-ui/style';
import { formatPostDate } from '@mummssoftware/utils/formatters';
import {
  ACTIVE,
  DISCONTINUED,
  Submitted,
} from '@mummssoftware/utils/constants';
import { isTaskSelected, isTaskModified } from './utils';
import {
  SET_CPP_TASKS,
  SET_SUBMIT_PLAN_ACTIVE,
  Editing,
} from '../Context/constants';
import { CPPContext } from '../Context/CPPContext';
import { DCButton } from '../CPPEngine/Field';
import { useStyles } from './style';
import { TaskCountCellRenderer } from '../CarePlanProblems/CellRenderer';
import {
  TaskDiv,
  TaskNoteDiv,
  TaskTextDiv,
} from '../../components/layout/WrapperDiv';
import { Context } from '../../context';

type SingleTaskPlannerProps = {
  task: mumms.PatientCppTask;
  editTaskMode?: boolean;
};
/**
 * @note decided w Ashley
 * 1. The user can only uncheck a checkbox
 * 2. Internal logic decide if a checkbox should be checked or not
 * 3. if completing all fields, the checkbox becomes checked,
 * if unchecking, need to change a parameter for it to be checked again
 */
export const SingleTaskPlanner = React.memo(
  ({ task, editTaskMode }: SingleTaskPlannerProps) => {
    const {
      cppForm,
      initialCppTasks,
      submitPlanActive,
      submitPlan,
      dispatch,
      otherTask,
    } = useContext(CPPContext);
    // individual single task state
    const [cppTask, setCppTask] = useState<mumms.PatientCppTask>(task);

    /**
     * submit Form dispatched from Widget Header
     * if task dispatch to context
     * @note what happens if 200 tasks dispatched at the same time?
     * @note dispatch the task when done => performance issue?
     */
    useEffect(() => {
      if (submitPlan && task) {
        if (editTaskMode) {
          if (isTaskSelected(cppTask)) {
            const payload = { ...cppTask, editTaskMode };
            dispatch({
              type: SET_CPP_TASKS,
              payload,
            });
          }
        } else {
          dispatch({
            type: SET_CPP_TASKS,
            payload: cppTask,
          });
        }
      }
    }, [submitPlan]);

    // task can only be DC if the plan has already been submitted
    // if in Progress or not yet submitted, you can toggle the checkbox
    const isTaskDCable =
      cppForm && cppForm.status && cppForm.status === Submitted;
    const { currentUser } = useContext(Context);
    const classes = useStyles();

    const emptyValue = {
      label: '',
      value: '',
    };
    const { who, frequency, period } = cppTask;
    // if answers, build the default answer for EDIT mode
    const whoAnswer = who
      ? {
          label: who,
          value: who,
        }
      : null;
    const frequencyAnswer =
      frequency && frequency !== 'null'
        ? {
            label: frequency,
            value: frequency,
          }
        : null;
    const periodAnswer = period
      ? {
          label: period,
          value: period,
        }
      : null;

    // check if only one option
    const whoValues = cppTask.whovalues.split(',');
    const defaultWhoOptionText =
      whoValues && whoValues.length && whoValues.length === 1
        ? whoValues[0]
        : null;

    const defaultWhoOption = defaultWhoOptionText
      ? {
          label: defaultWhoOptionText,
          value: defaultWhoOptionText,
        }
      : null;

    const getOptions = (values: string) => {
      const tasks = values.split(',');
      const options = tasks.length
        ? tasks.map((tasktext: string) => ({
            label: tasktext,
            value: tasktext,
          }))
        : [];
      return options;
    };

    /**
     * Need to update the task info upon creation
     * if editing the plan only the last user info
     */
    const getTaskInfo = (ptTask: mumms.PatientCppTask) => {
      const taskInfo = {
        ...ptTask,
        cpptask_id: ptTask.cpptask_id || ptTask.id,
        statusdatetime: ptTask.statusdatetime || formatPostDate(Date.now()),
        createpersonid: ptTask.createpersonid || currentUser.userId,
        createdatetime: ptTask.createdatetime || formatPostDate(Date.now()),
        modifiedpersonid: currentUser.userId,
        modifieddatetime: hasTaskBeenModified
          ? formatPostDate(Date.now())
          : ptTask.modifieddatetime,
      };
      return taskInfo;
    };

    const [taskValue, setValue] = useState<string>('');
    const hasTaskValue = taskValue.trim() !== '';

    const handleTextChange = (event: any) => {
      setValue(event.target.value);
    };

    const getTaskInput = () => (
      <SuggestBoxWrapper width={180}>
        <TextField
          id="text-field-cpp"
          variant="filled"
          multiline
          value={taskValue}
          onChange={handleTextChange}
          onBlur={onTaskTextChange}
          InputProps={{
            classes: { root: classes.rootOtherInput },
          }}
        />
      </SuggestBoxWrapper>
    );

    const getTaskText = (cpptask: mumms.PatientCppTask) =>
      editTaskMode ? (
        getTaskInput()
      ) : (
        <TaskTextDiv id="task-text" width={180}>
          {cpptask.tasktext}
        </TaskTextDiv>
      );

    // eslint-disable-next-line no-param-reassign
    const frequencyData = [...Array(30).keys()].map((x) => ++x);
    const frequencyOptions = frequencyData.map((tasktext: number) => ({
      label: tasktext,
      value: tasktext,
    }));

    // discontinued tasks should not be checked
    const isTaskDiscontinued = cppTask.status === DISCONTINUED;
    const isAlreadyChecked = isTaskSelected(cppTask) && !isTaskDiscontinued;
    // initialTask to know the initial state of task
    const initialTask = find(initialCppTasks, {
      id: cppTask.id,
    });
    // initial state => selected if task was already selected but not discontinued
    const isInitialChecked = isTaskSelected(initialTask) && !isTaskDiscontinued;
    /**
     * has task been modified so modifieddate can be updated accordingly
     * 1. task modified if task is already checked and changed
     * 2. task was not already checked
     */
    const hasTaskBeenModified = isAlreadyChecked
      ? isTaskModified(initialTask, cppTask)
      : true;
    /**
     * all 3 options who frequency period states
     */
    const [checked, setChecked] = useState<boolean>(isAlreadyChecked);
    // remove defaultWhoOption because want to check the checkbox before
    const unchecked_who_option = whoAnswer || emptyValue;
    const checked_who_option = whoAnswer || defaultWhoOption || emptyValue;
    const [whoOption, setWhoOption] = useState<mumms.OptionType>(
      unchecked_who_option,
    );
    const [frequencyOption, setFreqOption] = useState<mumms.OptionType>(
      frequencyAnswer || emptyValue,
    );
    const [periodOption, setPeriodOption] = useState<mumms.OptionType>(
      periodAnswer || emptyValue,
    );
    const [discontinuedChecked, setDiscontinuedChecked] = useState<boolean>(
      false,
    );
    const [checkedTaskModified, setCheckedTaskModified] = useState<boolean>(
      false,
    );

    /**
     * On Load if default option then populate it
     */
    useEffect(() => {
      if (defaultWhoOption) {
        setWhoOption(checked_who_option);
        const payload = {
          ...cppTask,
          who: checked_who_option.value,
        };
        setCppTask(payload);
      }
    }, []);

    /**
     * empty options for discontinued tasks
     */
    useEffect(() => {
      if (isTaskDiscontinued) {
        setWhoOption(emptyValue);
        setFreqOption(emptyValue);
        setPeriodOption(emptyValue);
      }
    }, []);

    const setTaskActive = () => {
      setChecked(true);
      const payload = {
        ...getTaskInfo(cppTask),
        // isselected determines if the tasks gets saved
        isselected: 'true',
        status: ACTIVE,
      };

      setCppTask(payload as any);

      if (!submitPlanActive && !isAlreadyChecked) {
        dispatch({
          type: SET_SUBMIT_PLAN_ACTIVE,
          payload: true,
        });
      }
    };

    /**
     * If all options filled out for on task
     * -> save the task indicator and check the checkbox
     * -> set the submit plan active
     */
    useEffect(() => {
      let allOptionsFilled: boolean;
      const basicOptionsFilled = !!(
        whoOption.value &&
        frequencyOption.value &&
        periodOption.value
      );
      if (editTaskMode) {
        allOptionsFilled = basicOptionsFilled && hasTaskValue;
      } else {
        allOptionsFilled = basicOptionsFilled;
      }
      if (allOptionsFilled) {
        // if you change an option of an already checked task
        if (checkedTaskModified) {
          !submitPlanActive &&
            dispatch({
              type: SET_SUBMIT_PLAN_ACTIVE,
              payload: true,
            });
          // if a task is DCed and you change the who
        } else if (isTaskDiscontinued) {
          if (discontinuedChecked) {
            setTaskActive();
          }
          // do not check the task
        } else {
          setTaskActive();
        }
      } else if (editTaskMode) {
        if (checked) {
          setChecked(false);
        }
      }
    }, [
      whoOption.value,
      frequencyOption.value,
      periodOption.value,
      hasTaskValue,
    ]);

    /**
     * If you change an option of an already checked task
     */
    const registerCheckedTaskChange = () => {
      if (!submitPlanActive && isAlreadyChecked) {
        setCheckedTaskModified(true);
      }
    };

    /**
     * option change handlers
     */
    const onWhoChange = (option: any) => {
      setWhoOption(option);
      const payload = {
        ...cppTask,
        who: option.value,
      };
      setCppTask(payload);
      // the task is discontinued and you are changing the who
      if (isTaskDiscontinued) {
        setDiscontinuedChecked(true);
      }
      registerCheckedTaskChange();
    };
    const onFrequencyChange = (option: any) => {
      setFreqOption(option);
      const payload = {
        ...cppTask,
        frequency: option.value,
      };
      setCppTask(payload);
      registerCheckedTaskChange();
    };
    const onPeriodChange = (option: any) => {
      setPeriodOption(option);
      const payload = {
        ...cppTask,
        period: option.value,
      };
      setCppTask(payload);
      registerCheckedTaskChange();
    };
    const onTaskTextChange = (e: any) => {
      const taskTextValue = e.target.value;
      const payload = {
        ...cppTask,
        taskid: undefined,
        id: '',
        cpptask_id: (otherTask as any)[0].id,
        userprovideddisplayname: taskTextValue,
        // correct for Editing
        patientcareplanproblem_id: cppForm ? cppForm.id : '',
      };
      setCppTask(payload);
    };

    /**
     * Logic w DC and being able to toggle freely
     * You can only UNCHECK a task
     */
    const onCheckBoxChange = (event: any) => {
      const isChecked = (event.target as any).checked;
      /**
       * cantUncheck means you need to DC
       * if the plan has already been submitted and it was checked, you need to DC
       * Need to know the form status (Submitted), the initial state of the form (was checked )
       * and the checkbox state (checked or not)
       */
      const cantUncheck = isTaskDCable && isInitialChecked && !isChecked;
      // you cannot unselect a checkbox, need to DC
      // you can only uncheck a checkbox if you have not submitted the plan
      // => !isChecked means you are unchecking
      // => isChecked && !cantUncheck means you are unchecking a non submitted plan yet
      if (!isChecked && !cantUncheck) {
        setChecked(isChecked);
        const payload = {
          ...cppTask,
          who: '',
          frequency: '',
          period: '',
          isselected: 'false',
          status: '',
        };

        setCppTask(payload as any);
        !submitPlanActive &&
          dispatch({
            type: SET_SUBMIT_PLAN_ACTIVE,
            payload: true,
          });
      }
    };

    const onDCChange = () => {
      if (checked) {
        setChecked(false);
        const payload = {
          ...cppTask,
          status: DISCONTINUED,
          statusdatetime: formatPostDate(Date.now()),
        };
        setCppTask(payload);
        !submitPlanActive &&
          dispatch({
            type: SET_SUBMIT_PLAN_ACTIVE,
            payload: true,
          });
      }
    };

    // todo DCdisabled in STATE
    // const [DCdisabled, setDCdisabled] = useState<boolean>(true);
    let DCdisabled;
    // isDCDisabled is active if the task had been submitted and it is initially checked
    if (cppTask.status === DISCONTINUED) {
      DCdisabled = true;
    } else if (!isTaskDCable) {
      DCdisabled = true;
    } else {
      if (isInitialChecked) {
        DCdisabled = !checked;
      } else {
        DCdisabled = true;
      }
    }

    return (
      <TaskDiv>
        {cppForm?.type === Editing && (
          <TaskNoteDiv>
            <TaskCountCellRenderer data={task} />
          </TaskNoteDiv>
        )}

        <DCButton
          key={`field-${cppTask.id}`}
          outsideDC
          disabled={DCdisabled}
          handleChange={onDCChange}
        />
        <Checkbox
          checked={checked}
          onChange={onCheckBoxChange}
          color="primary"
          classes={{
            root: classes.defaultColor,
          }}
          inputProps={{
            'aria-label': 'controlled',
          }}
        />
        <SuggestBoxWrapper width={90}>
          <SuggestBox
            fitcontent
            options={getOptions(cppTask.whovalues)}
            value={whoOption}
            onChange={onWhoChange}
          />
        </SuggestBoxWrapper>

        {getTaskText(cppTask)}
        <SuggestBox
          fitcontent
          options={frequencyOptions}
          value={frequencyOption}
          onChange={onFrequencyChange}
        />
        <TaskTextDiv>time(s) per</TaskTextDiv>
        <SuggestBox
          fitcontent
          options={getOptions(cppTask.periodvalues)}
          value={periodOption}
          onChange={onPeriodChange}
        />
      </TaskDiv>
    );
  },
);
